import Vue from 'vue'
import Vuex from 'vuex'
import router from '../router/index';
import { retrySeveralTimes } from '../utils/refetch';
import { apiRoutes, apiUrl, extractApiList, mergeApiHeaders } from '../utils/api';
import { dedupeOpenBatchesByFarm, fetchConsumeByBatchReport } from '../utils/feedInventory';
import { generateProfileReport } from '../excel/excelProfileHistory';
import { getMinSiloLevelVariationPercent } from '../utils/siloLevelVariationFilter';
import {
  historyRangeChunks,
  historyResponseLooksTruncated,
  historyUncoveredEdges,
  inclusiveHistoryEnd,
  mergeProfileHistory,
} from '../utils/siloProfileHistoryRange';
import { mapPenWeightsResponse, mergePenWeightSeries, normalizeIndividualWeighingPayload } from '../utils/individual-weighing';
import { parseISO, format, isValid } from 'date-fns';

const normalizeDateTimeForApi = (dateStr) => {
  if (!dateStr) return dateStr;
  const normalizedInput = String(dateStr).trim().replace(' ', 'T');
  let parsedDate = parseISO(normalizedInput);
  if (!isValid(parsedDate)) {
    parsedDate = new Date(dateStr);
  }
  if (!isValid(parsedDate)) return dateStr;
  return format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss");
};

async function fetchSiloProfileHistoryWindow(headers, sensorPk, startDate, endDate) {
  const response = await fetch(`${URL}silo_profiles/silo_profile_history/?include_curves=false`, {
    method: 'POST',
    body: JSON.stringify({
      start_date: startDate,
      end_date: endDate,
      sensor_pk: sensorPk,
      bypass_validation: true,
    }),
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const json = await response.json();
  return Array.isArray(json) ? json : (json?.results ?? []);
}

Vue.use(Vuex)

const configuredApiUrl = process.env.VUE_APP_URL && String(process.env.VUE_APP_URL).trim()
export const URL = configuredApiUrl || "https://api.pecsmart.cloud/"
export const URL_CAMV3 = "https://camv3api.pecsmart.cloud/"

const configuredIndividualWeighingUrl = process.env.VUE_APP_INDIVIDUAL_WEIGHING_URL
  && String(process.env.VUE_APP_INDIVIDUAL_WEIGHING_URL).trim()
export const URL_INDIVIDUAL_WEIGHING = configuredIndividualWeighingUrl || "http://0.0.0.0:8000/api/pesagem-corral"

/** Align fetch URLs with the protocol configured in VUE_APP_URL (http vs https). */
export const alignUrlWithApiBase = (fetchUrl) => {
  if (!fetchUrl) return fetchUrl;
  const apiBase = URL || '';
  if (apiBase.startsWith('http://') && fetchUrl.startsWith('https://')) {
    return fetchUrl.replace('https://', 'http://');
  }
  if (apiBase.startsWith('https://') && fetchUrl.startsWith('http://')) {
    return fetchUrl.replace('http://', 'https://');
  }
  return fetchUrl;
};

/** Build the Authorization header object from a token string. */
const buildAuthHeader = (token) => token ? {
  'Accept': 'application/json',
  'Content-Type': 'application/json',
  'Authorization': `Token ${token}`
} : null;

export default new Vuex.Store({
  state: {
    Farms: [],
    Barns: null,
    Batchs: [],
    companies: [],
    sensorsListFeed: [],
    sensorsListMic: [],
    sensorsListCam: [],
    batchDetail: [],
    stateBarns: [],
    curveWeightReference: [],
    curveWeightReferenceByAnimalType: {},
    activeRequests : 0,
    historyBatch : [],
    selectedFarms: [],
    smartCamData:[],
    smartCamDataByBatch: {},
    allBarns:[],
    companySettings: [],
    individualWeighingData: {},
    individualWeighingDataByBarn: {},
    lossesBatch:[],
    lossesBatchByBatch: {},
    foodByCompany: null,
    foodsByCompany: {},
    lossesChartStatus: false,
    currentUser: null,
    logedIn: false,
    error: false,
    weightChartStatus: true,
    standardDeviationChartStatus: false,
    uniformityChartStatus: false,
    gpdWeekChartStatus: false,
    gpdAccChartStatus: false,
    header: null,
    smartMicData: [],
    smartFeedData: [],
    smartFeedDataByKey: {},
    navbar: false,
    barn_target: null,
    farm_target: null,
    batch_target: null,
    targetCompany: null,
    allBatches: [],
    isAdmin: null,
    userLevel: null,
    smartFeedConsolidatedData: [],
    smartFeedConsolidatedDataByKey: {},
    smartFeedChartDataByKey: {},
    animalTypes: null,
    errorMessage: null,
    dataCamv3: [],
    selectedCompany: null,
    selectedFarm: null,
    selectedFarmPecsmart: null,
    showByFarm: false,
    showByCompanies: false,
    siloDataByBarn: {},
    siloDataHomeByBarn: {},
    sensorLoadingStates: {},
    homeFarmBarnsCache: {},
    homeBarnDataCache: {},
    accFoodDataByBarn: {},
    siloSettingsBySilo: {},
    siloProfileHistoryByKey: {},
    siloProfileCurvesMapByKey: {},
    siloLastProfileBySiloByKey: {},
    feedInventoryBatchesByCompany: {},
    feedInventoryBarnSensorsByBarn: {},
    feedInventoryConsumeByBatch: {},
    feedInventoryDiasPrioridadeByBatch: {},
    feedInventoryLoadingByBatch: {},
    feedInventoryBatchesLoadingByCompany: {},
    feedInventoryFetchedByCompany: {},
  },
  mutations: {
    setDefaultValues(state) {
      state.Farms = null
      state.currentUser = null
      state.selectedFarms =  null
      state.logedIn =  false
      state.error = false
      state.header = null
      state.smartMicData = []
      state.smartFeedData = []
      state.smartFeedDataByKey = {}
      state.navbar = false
      state.barn_target = null
      state.farm_target = null
      state.batch_target =  null
      state.targetCompany = null
      state.allBatches = []
      state.isAdmin = null
      state.userLevel = null
      state.smartFeedConsolidatedData = []
      state.smartFeedConsolidatedDataByKey = {}
      state.smartFeedChartDataByKey = {}
      state.animalTypes = null
      state.sensorsListFeed = []
      state.sensorsListMic = []
      state.sensorsListCam = []
      state.curveWeightReference = []
      state.curveWeightReferenceByAnimalType = {}
      state.smartCamData = []
      state.smartCamDataByBatch = {}
      state.companySettings = []
      state.selectedCompany = null
      state.selectedFarm = null
      state.selectedFarmPecsmart = null
      state.showByFarm = false
      state.showByCompanies = false
      state.siloDataByBarn = {}
      state.siloDataHomeByBarn = {}
      state.sensorLoadingStates = {}
      state.foodsByCompany = {}
      state.homeFarmBarnsCache = {}
      state.homeBarnDataCache = {}
      state.accFoodDataByBarn = {}
      state.smartFeedData = []
      state.smartFeedDataByKey = {}
      state.smartFeedConsolidatedData = []
      state.smartFeedConsolidatedDataByKey = {}
      state.smartFeedChartDataByKey = {}
      state.siloSettingsBySilo = {}
      state.siloProfileHistoryByKey = {}
      state.siloProfileCurvesMapByKey = {}
      state.siloLastProfileBySiloByKey = {}
      state.feedInventoryBatchesByCompany = {}
      state.feedInventoryBarnSensorsByBarn = {}
      state.feedInventoryConsumeByBatch = {}
      state.feedInventoryDiasPrioridadeByBatch = {}
      state.feedInventoryLoadingByBatch = {}
      state.feedInventoryBatchesLoadingByCompany = {}
      state.feedInventoryFetchedByCompany = {}
    },
    setFeedInventoryBatchesByCompany(state, { company, batches }) {
      state.feedInventoryBatchesByCompany[company] = batches
    },
    clearFeedInventoryBatches(state, company) {
      Vue.set(state.feedInventoryBatchesByCompany, company, [])
    },
    addFeedInventoryBatch(state, { company, batch }) {
      const current = state.feedInventoryBatchesByCompany[company] || []
      if (current.some(item => item.pk === batch.pk)) return
      Vue.set(state.feedInventoryBatchesByCompany, company, [...current, batch])
    },
    setFeedInventoryBatchesLoading(state, { company, loading }) {
      state.feedInventoryBatchesLoadingByCompany[company] = loading
    },
    setFeedInventoryFetched(state, { company, fetched }) {
      state.feedInventoryFetchedByCompany[company] = fetched
    },
    setFeedInventoryBarnSensors(state, { barnPk, sensors }) {
      Vue.set(state.feedInventoryBarnSensorsByBarn, barnPk, sensors)
    },
    setFeedInventoryConsume(state, { batchPk, data }) {
      state.feedInventoryConsumeByBatch[batchPk] = data
    },
    setFeedInventoryDiasPrioridade(state, { batchPk, dias }) {
      Vue.set(state.feedInventoryDiasPrioridadeByBatch, batchPk, dias)
    },
    setFeedInventoryBatchLoading(state, { batchPk, loading }) {
      Vue.set(state.feedInventoryLoadingByBatch, batchPk, loading)
    },
    cleanData(state) {
      state.curveWeightReference = []
      state.smartCamData = []
      state.smartCamDataByBatch = {}
      state.activeRequests = 0
      state.allBarns = []
      state.historyBatch = []
      state.batch_target = null
      state.barn_target = null
      state.farm_target = null
      state.batchDetail = []
      state.lossesBatch = []
      state.lossesBatchByBatch = {}
      state.dataCamv3 = []
      state.homeFarmBarnsCache = {}
      state.homeBarnDataCache = {}
      state.accFoodDataByBarn = {}
      state.siloSettingsBySilo = {}
      state.siloProfileHistoryByKey = {}
      state.siloProfileCurvesMapByKey = {}
      state.siloLastProfileBySiloByKey = {}
    },
    cleanTarget(state) {
      state.batch_target = null
      state.barn_target = null
      state.farm_target = null
      state.batchDetail = []
    },
    cleanFazendas(state){
      state.sensorsListMic = []
      state.sensorsListCam = []
      state.historyBatch = []
    },
    setCamData(state, results) {
      state.smartCamData = results
    },
    setCamDataByBatch(state, { batchPk, data, lastUpdated }) {
      if (batchPk == null) return
      state.smartCamDataByBatch[String(batchPk)] = {
        data: Array.isArray(data) ? data : [],
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setSensorsListFeed(state, results) {
      const barnPk = results?.barn?.pk
      const incomingData = Array.isArray(results?.data) ? results.data.filter(Boolean) : []
      if (barnPk == null || incomingData.length === 0) return

      const normalizeSensorId = (item) => item?.sensor ?? item?.pk ?? item?.id ?? null
      const normalizeSiloId = (item) => item?.silo?.pk ?? item?.silo?.id ?? item?.silo ?? null

      let feed = state.sensorsListFeed.find((obj) => obj?.barn?.pk == barnPk)
      if (feed === undefined) {
        feed = {
          barn: results.barn,
          batch: results.batch ?? null,
          sensor: results.sensor ?? [],
          data: []
        }
        state.sensorsListFeed.push(feed)
      } else {
        if (results.batch != null) feed.batch = results.batch
        if (Array.isArray(results.sensor) && results.sensor.length > 0) {
          feed.sensor = results.sensor
        }
      }

      incomingData.forEach((entry) => {
        const sensorId = normalizeSensorId(entry)
        const siloId = normalizeSiloId(entry)
        const currentIndex = feed.data.findIndex((existing) => {
          const existingSensorId = normalizeSensorId(existing)
          const existingSiloId = normalizeSiloId(existing)
          return String(existingSensorId) === String(sensorId) && String(existingSiloId) === String(siloId)
        })

        if (currentIndex === -1) {
          feed.data.push(entry)
          return
        }

        feed.data.splice(currentIndex, 1, {
          ...feed.data[currentIndex],
          ...entry
        })
      })
    },
    setSensorsListMic(state, results) {
      const batchPk = results?.batch?.pk ?? results?.batch?.id
      const barnPk = results?.barn?.pk ?? results?.barn?.id
      const index = state.sensorsListMic.findIndex((item) => {
        if (batchPk != null) {
          const itemBatch = item?.batch?.pk ?? item?.batch?.id
          return itemBatch != null && String(itemBatch) === String(batchPk)
        }
        if (barnPk != null) {
          const itemBarn = item?.barn?.pk ?? item?.barn?.id
          return itemBarn != null && String(itemBarn) === String(barnPk)
        }
        return false
      })
      if (index === -1) {
        state.sensorsListMic.push(results)
        return
      }
      state.sensorsListMic.splice(index, 1, results)
    },
    setSensorsListCam(state, results) {
      state.sensorsListCam.push(results)
    },
    setBatchDetail(state, result) {
      state.batchDetail = result
    },
    setCurveWeightReference(state, result) {
      state.curveWeightReference = result 
    },
    setCurveWeightReferenceByAnimalType(state, { animalType, curve, lastUpdated }) {
      if (animalType == null) return
      state.curveWeightReferenceByAnimalType[animalType] = {
        curve: Array.isArray(curve) ? curve : [],
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    incrementActiveRequests(state) {
      state.activeRequests++;
    },
    decrementActiveRequests(state) {
      state.activeRequests--;
    },
    setSensorFeedTarget(state, results) {
      state.sensorFeedTarget.push = results
    },
    setFarms(state, Farm) {
      state.Farms = Farm
    },
    setAllBarns(state, barn) {
      const incomingFarmPk = barn?.farm?.farm?.pk ?? barn?.farm?.farm ?? barn?.farm?.pk ?? null
      if (incomingFarmPk == null) {
        state.allBarns.push(barn)
        return
      }
      const index = state.allBarns.findIndex((item) => {
        const farmPk = item?.farm?.farm?.pk ?? item?.farm?.farm ?? item?.farm?.pk ?? null
        return String(farmPk) === String(incomingFarmPk)
      })
      if (index === -1) {
        state.allBarns.push(barn)
      } else {
        state.allBarns.splice(index, 1, barn)
      }
    },
    setHomeFarmBarnsCache(state, { farmPk, barns, lastUpdated }) {
      if (farmPk == null) return
      state.homeFarmBarnsCache[farmPk] = {
        barns: Array.isArray(barns) ? barns : [],
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setHomeBarnDataCache(state, { barnPk, sensors, batch, lastUpdated }) {
      if (barnPk == null) return
      const current = state.homeBarnDataCache[barnPk] || {}
      state.homeBarnDataCache[barnPk] = {
        sensors: Array.isArray(sensors) ? sensors : (current.sensors || []),
        batch: Array.isArray(batch) ? batch : (current.batch || []),
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setHistoryBatch(state, batch) {
      batch.forEach(element => {
        const index = state.historyBatch.findIndex((item) => item.pk === element.pk)
        if (index >= 0) {
          state.historyBatch.splice(index, 1, element)
        } else {
          state.historyBatch.push(element)
        }
      })
    },
    updateBatchTarget(state, newBatch) {
      state.batch_target = newBatch
    },
    setCompanySettings(state, settings){
      state.companySettings = settings
    },
    setIndividualWeighingData(state, data) {
      state.individualWeighingData = data
    },
    setIndividualWeighingDataByBarn(state, { barnPk, data }) {
      state.individualWeighingDataByBarn = { ...state.individualWeighingDataByBarn, [barnPk]: data }
    },
    mergeIndividualWeighingPenSeries(state, { barnPk, pen, payload }) {
      const merged = mergePenWeightSeries(state.individualWeighingData, payload, pen)
      if (!merged || merged === state.individualWeighingData) return
      state.individualWeighingData = merged
      if (barnPk != null) {
        state.individualWeighingDataByBarn = {
          ...state.individualWeighingDataByBarn,
          [String(barnPk)]: merged,
        }
      }
    },
    setCompanies(state, companies) {
      state.companies = companies;
    },
    setLossesBatch(state, data) {
      state.lossesBatch.push(...data.results)
    },
    setLossesBatchData(state, data) {
      state.lossesBatch = Array.isArray(data) ? data : []
    },
    setLossesBatchByBatch(state, { batchPk, losses, lastUpdated }) {
      if (batchPk == null) return
      state.lossesBatchByBatch[String(batchPk)] = {
        losses: Array.isArray(losses) ? losses : [],
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setFoodByCompany(state, value) {
      state.foodByCompany = Array.isArray(value) ? value : (value?.results || [])
    },
    setFoodsByCompany(state, { companyPk, foods }) {
      state.foodsByCompany[companyPk] = foods
    },
    updateStateByBarn(state, value) {
      state.stateBarns.forEach((obj)=>{
        if (obj.pk == value.pk && value.sensor == 'smartmic') {
          obj.finishLoadMic = true
        }
        if (obj.pk == value.pk && value.sensor == 'smartcam') {
          obj.finishLoadCam = true
        }
        if (obj.pk == value.pk && value.sensor == 'smartfeed') {
          obj.finishLoadFeed = true
        }
      })
    },
    setStateBarn(state, barn){
      state.stateBarns.push(barn)
    },
    setCurrentUser(state, user) {
      state.currentUser = user
    },
    setCurrentEmployee(state, employee) {
      state.currentEmployee = employee
    },
    setFarm(state, Farm) {
      state.Farms = Farm
    },
    setSelectedFarms(state, value) {
      state.selectedFarms = value
    },
    setLogStatus(state, value) {
      state.logedIn = value
    },
    setError(state, status) {
      state.error = status
    },
    setNavbar(state, value) {
      state.navbar = value
    },
    clearChart(state) {
      state.weightChartStatus = false,
      state.standardDeviationChartStatus = false,
      state.uniformityChartStatus = false
      state.gpdWeekChartStatus = false,
      state.gpdAccChartStatus = false
      state.lossesChartStatus = false;
    },
    chartDisplay(state, chart) {
      if (chart === 'weight') {
        this.commit("clearChart")
        state.weightChartStatus = true
      }
      if (chart === 'standardDeviation') {
        this.commit("clearChart")
        state.standardDeviationChartStatus = true
      }
      if (chart === 'uniformity') {
        this.commit("clearChart")
        state.uniformityChartStatus = true
      }
      if (chart === 'gpdWeek') {
        this.commit("clearChart")
        state.gpdWeekChartStatus = true
      }
      if (chart === 'gpdAcc') {
        this.commit("clearChart")
        state.gpdAccChartStatus = true
      }
      if (chart === "losses") {
        this.commit("clearChart");
        state.lossesChartStatus = true;
      }
    },
    setHeader(state, token) {
      state.header = buildAuthHeader(token)
    },
    setLoadingStateSmartFeed(state, boolean) {
      state.loadingStateSmartFeed = boolean
    },
    setSmartMicData(state, results) {
      state.smartMicData = []
      state.smartMicData.push(results)
    },
    setSmartFeedData(state, results) {
      const index = state.smartFeedData.findIndex(item =>
        String(item?.silo) === String(results?.silo) &&
        String(item?.barn) === String(results?.barn)
      )
      if (index === -1) {
        state.smartFeedData.push(results)
      } else {
        state.smartFeedData.splice(index, 1, results)
      }
    },
    setSmartFeedDataByKey(state, { key, data, lastUpdated }) {
      if (!key) return
      state.smartFeedDataByKey[key] = {
        data: data || null,
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setErrorMessage(state, message) {
      state.errorMessage = message
    },
    setNewBarn(state, barn) {
      for (let farm in state.Farms) {
        if (state.Farms[farm].farm === barn.farm) {
          state.Farms[farm].barns.push(barn)
        }
      }
    },
    setNewFarm(state, farm) {
      state.Farms.push({
        farm: farm.pk,
        name: farm.name,
        barns: [],
      })
    },
    updateEditBarn(state, {current_barn, name}) {
      for (let farm in state.Farms) {
        for (let barn in state.Farms[farm].barns) {
          if (state.Farms[farm].barns[barn].pk === current_barn.pk) {
            state.Farms[farm].barns[barn].name = name
          }
        }
      }
    },
    setNewBatch(state, {barn_pk, batch, batch_curve}) {
      for (let farm in state.Farms) {
        const barn = state.Farms[farm].barns.find(barn => barn.pk === barn_pk)
        if (barn) {
          barn.batch = batch
          barn.batch.reference_weight_curve = batch_curve
          barn.batch.batch_losses = barn.batch.batch_losses.length
          state.batch_target = barn.batch
        }
      }
    },
    updateEndBatch(state, {batch, date_departure, final}) {
      let isAnOldBatch = true
      for (let farm in state.Farms) {
        for (let barn in state.Farms[farm].barns) {
          if (state.Farms[farm].barns[barn].batch.id === batch.id) {
            state.Farms[farm].barns[barn].batch.date_departure = date_departure
            state.Farms[farm].barns[barn].batch.average_final_weight = final
            state.Farms[farm].barns[barn].batch.final_count = batch.initial_count - batch.batch_losses
            state.batch_target = state.Farms[farm].barns[barn].batch
            isAnOldBatch = false
          }
        }
      }
      if (isAnOldBatch) {
        for (let farm in state.allBatches) {
          for (let barn in state.allBatches[farm].barns) {
            for (let old_batch in state.allBatches[farm].barns[barn].batches) {
              if (state.allBatches[farm].barns[barn].batches[old_batch].id === batch.id) {
                state.allBatches[farm].barns[barn].batches[old_batch].date_departure = date_departure
                state.allBatches[farm].barns[barn].batches[old_batch].average_final_weight = final
                state.allBatches[farm].barns[barn].batches[old_batch].final_count = batch.initial_count - batch.batch_losses
                state.batch_target = state.allBatches[farm].barns[barn].batches[old_batch]
              }
            }
          }
        }
      }
    },
    updateEditBatch(state, {batch_object, new_date_accommodation, initial, desired, count, date_birth}) {
      for (let farm in state.Farms) {
        for (let barn in state.Farms[farm].barns) {
          let batch = null
          if (state.Farms[farm].barns[barn].batch?.id === batch_object.id) {
            batch = state.Farms[farm].barns[barn].batch
          }

          if (batch) {
            batch.average_initial_weight = initial
            batch.desired_weight = desired
            batch.initial_count = count
            batch.date_accomodation = new_date_accommodation
            if (date_birth) batch.date_birth = date_birth
          }
        }
      }
    },
    updateEditFarm(state, {current_farm, name}) {
      for (let farm_target in state.Farms) {
        if (state.Farms[farm_target].farm === current_farm.farm) {
          state.Farms[farm_target].name = name
        }
      }
    },
    setBarnTarget(state, barnOrPayload) {
      const isWrapped = barnOrPayload && typeof barnOrPayload === 'object'
        && 'barn' in barnOrPayload && 'farmPk' in barnOrPayload
      state.barn_target = isWrapped ? barnOrPayload.barn : barnOrPayload
      state.farm_target = isWrapped ? (barnOrPayload.farmPk ?? null) : null
    },
    setBatchTarget(state, batch) {
      state.batch_target = batch
    },
    setTargetCompany(state, company) {
      state.targetCompany = company
    },
    setAllBatches(state, history) {
      state.allBatches = history
    },
    setIsAdmin(state, role) {
      if (role === "pecsmart_admin" || role === "company_admin" || role === "farm_admin") {
        state.isAdmin = true
      }
      else {
        state.isAdmin = false
      }
    },
    setUserLevel(state, role) {
      if (role === "pecsmart_admin" || role === "pecsmart_employee") {
        state.userLevel = "pecsmart"
      }
      if (role === "company_admin" || role === "company_employee") {
        state.userLevel = "company"
      }
      if (role === "farm_admin" || role === "farm_employee") {
        state.userLevel = "employee"
      }
    },
    setSmartFeedConsolidatedData(state, data) {
      state.smartFeedConsolidatedData = [data]
    },
    setSmartFeedConsolidatedDataByKey(state, { key, data, lastUpdated }) {
      if (!key) return
      state.smartFeedConsolidatedDataByKey[key] = {
        data: data || null,
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setSmartFeedChartDataByKey(state, { key, data, lastUpdated }) {
      if (!key) return
      state.smartFeedChartDataByKey[key] = {
        data: data || null,
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setAnimalTypes(state, data) {
      state.animalTypes = data.results
    },
    setCamV3Data(state, data) {
      state.dataCamv3 = data
    },
    updateLossesDelete(state, data) {
      const updatedData = state.lossesBatch.filter(item => item.pk !== data.pk)
      state.lossesBatch = updatedData
    },
    updateLossesEdit(state, data) {
      const index = state.lossesBatch.findIndex(item => item.pk === data.pk)
      if (index !== -1) {
        state.lossesBatch[index] = data
      }
    },
    // Nuevas mutaciones para persistencia de selects
    setSelectedCompany(state, value) {
      state.selectedCompany = value
    },
    setSelectedFarm(state, value) {
      state.selectedFarm = value
    },
    setSelectedFarmPecsmart(state, value) {
      state.selectedFarmPecsmart = value
    },
    setShowByFarm(state, value) {
      state.showByFarm = value
    },
    setShowByCompanies(state, value) {
      state.showByCompanies = value
    },
    setSiloDataByBarn(state, { barnPk, siloPk, data, lastUpdated }) {
      if (!state.siloDataByBarn[barnPk]) {
        state.siloDataByBarn[barnPk] = {}
      }
      state.siloDataByBarn[barnPk][siloPk] = {
        originalData: data,
        lastUpdated: lastUpdated
      }
    },
    setSiloDataHomeByBarn(state, { barnPk, siloPk, data, lastUpdated }) {
      if (!state.siloDataHomeByBarn[barnPk]) {
        state.siloDataHomeByBarn[barnPk] = {}
      }
      state.siloDataHomeByBarn[barnPk][siloPk] = {
        originalData: data,
        lastUpdated: lastUpdated
      }
    },
    setSensorLoadingState(state, { barnPk, sensorPk, isLoading }) {
      if (!state.sensorLoadingStates[barnPk]) {
        state.sensorLoadingStates[barnPk] = {}
      }
      state.sensorLoadingStates[barnPk][sensorPk] = isLoading
    },
    setAccFoodDataByBarn(state, { barnPk, startDateTime, endDateTime, siloProfiles, lastUpdated }) {
      if (barnPk == null) return
      state.accFoodDataByBarn[barnPk] = {
        startDateTime,
        endDateTime,
        siloProfiles: Array.isArray(siloProfiles) ? siloProfiles : [],
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setSiloSettingsBySilo(state, { siloPk, settings, lastUpdated }) {
      if (siloPk == null) return
      state.siloSettingsBySilo[siloPk] = {
        settings: Array.isArray(settings) ? settings : [],
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setSiloProfileHistoryByKey(state, { key, profiles, lastUpdated }) {
      if (!key) return
      state.siloProfileHistoryByKey[key] = {
        profiles: Array.isArray(profiles) ? profiles : [],
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setSiloProfileCurvesMapByKey(state, { key, curvesMap, lastUpdated }) {
      if (!key) return
      state.siloProfileCurvesMapByKey[key] = {
        curvesMap: curvesMap || {},
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    },
    setSiloLastProfileBySiloByKey(state, { key, profile, lastUpdated }) {
      if (!key) return
      state.siloLastProfileBySiloByKey[key] = {
        profile: profile || null,
        lastUpdated: lastUpdated || new Date().toISOString()
      }
    }
  },
  actions: {
    async setToken(state, user) {
      const data = {username: `${user.username}`, password: `${user.password}`}
      await fetch(`${URL}auth/login/`, {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify(data),
        headers: {
            "Content-type": "application/json; charset=UTF-8"
        }
      })
      .then(response => {
        if (response.ok === false) {
          throw new Error
        }
        return response.json()
      })
      .then(json => {
        window.localStorage.setItem("Token", json.key)
        window.localStorage.setItem("username", data.username)
        window.localStorage.removeItem("pecsmart-selects-state")
        state.commit("setHeader", window.localStorage.getItem("Token"))
      })
      .catch(() => state.commit("setError", true))
    },
    async registerLoss(state, {batch, weight, reason, death_details, datetime_registered, quantity}) {
      const data = {
        batch: batch,
        weight: weight,
        reason: `${reason}`,
        death_details: `${death_details}`,
        death_datetime: datetime_registered,
        quantity: quantity
      }
      await fetch(`${URL}losses/`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => {
        if (response.ok) {
          state.commit("setError", false)
          state.dispatch('fetchBatchDetail',batch)
        }
        else {
          state.commit("setError", true)
        }
      })
      .catch(() => state.commit("setError", true))
    },
    async registerBatch(state, {barn, animal_type, average_initial_weight, desired_weight, initial_count, date_accomodation, date_birth, days_life}) {

      let farm_target = state.getters.getAllBarns.find(obj =>{
        let barn_exist = obj.barn.find(element=> element.pk == barn)
        if (barn_exist != undefined) return obj
      })

      const reference_curve = await fetch(`${URL}reference_weight_curves/?company=${farm_target.farm.company}`, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        if (json.results.length !== 0) return {'pk': json.results[0].pk, 'curve': json.results[0].curve}
        return {'pk': null, 'curve': null}
      })

      const data = {
        average_initial_weight: average_initial_weight,
        average_final_weight: null,
        desired_weight: desired_weight,
        initial_count: initial_count,
        final_count: null,
        date_departure: null,
        date_birth: date_birth,
        days_life: days_life,
        batch_weights: [],
        reference_weight_curve: reference_curve.pk,
        batch_losses: [],
        batch_accommodation: [],
        post_process_weight: true,
        post_process_consume: true,
        barn: barn,
        animal_type: animal_type
      }

      const batch = await fetch(`${URL}batches/`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => json)

      const barn_pk = barn
      const batch_pk = batch.pk
      const date = date_accomodation
      const status = await state.dispatch("registerBatchAccomodation", {barn_pk, batch_pk, date})
      
      state.commit('updateBatchTarget', batch)
      state.dispatch('fetchBatchDetail', batch_pk)

      return {'ok': status, 'batch': batch}
    },
    async registerBatchAccomodation(state, {barn_pk, batch_pk, date}) {
      const data = {
        batch: batch_pk,
        barn: barn_pk,
        date_accommodation: date,
      }

      const response = await fetch(`${URL}batch_accommodations/`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => response.ok)
      .catch(() => false)

      return response
    },
    async registerBarn(state, {name, farm_pk}) {
      const data = {
        name: name,
        farm: farm_pk,
        batches: [],
        sensors: [],
        silos: [],
        batch_accommodation: [],
      }

      const response = await fetch(`${URL}barns/`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        return {'ok': true, 'data': json}
      })
      .catch(() => {
        return {'ok': false, 'data': null}
      })

      const barn_in_format = {
        "batch": response.data.batches,
        "farm": farm_pk,
        "feed_data": null,
        "mic_data": null,
        "name": response.data.name,
        "pk": response.data.pk,
        "sensors": response.data.sensors,
      }

      if (response.ok) {
        state.commit("setNewBarn", barn_in_format)
      }

      return response.ok
    },
    async editBarn(state, {name, current_barn}) {
      let old_barn = await fetch(`${URL}barns/${current_barn.pk}`, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())

      old_barn.name = name

      let response = await fetch(`${URL}barns/${current_barn.pk}/`, {
        method: "PUT",
        body: JSON.stringify(old_barn),
        headers: this.getters.getHeader
      })
      .then(response => response.ok)
      .catch(() => false)

      if (response) {
        state.commit("updateEditBarn", {current_barn, name})
      }

      return response
    },
    async updateBarnCoordinates({ getters }, { barnPk, lat, lng }) {
      const headers = getters.getHeader
      if (!headers || barnPk == null) return false

      try {
        const current = await fetch(apiUrl(`barns/${barnPk}/`), { headers })
        if (!current.ok) return false
        const barn = await current.json()
        barn.lat = lat
        barn.lng = lng
        const response = await fetch(apiUrl(`barns/${barnPk}/`), {
          method: "PUT",
          headers,
          body: JSON.stringify(barn),
        })
        return response.ok
      } catch (error) {
        console.error('Feed inventory: error saving barn coordinates', barnPk, error)
        return false
      }
    },
    async registerFarm(state, name) {
      const farm = {
        name: name,
        company: state.getters.getCurrentUser.company,
        barns: [],
        employess: []
      }

      const response = await fetch(`${URL}farms/`, {
        method: "POST",
        body: JSON.stringify(farm),
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => json)
      .catch(() => false)

      state.commit("setNewFarm", response)
      return true
    },
    async editAccommodation(state, {accommodation_pk, barn_pk, batch_pk, date}) {
      const data = {
        batch: batch_pk,
        barn: barn_pk,
        date_accommodation: date,
      }

      const response = await fetch(`${URL}batch_accommodations/${accommodation_pk}/`, {
        method: "PUT",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => response.ok)
      .catch(() => false)

      return response
    },
    async editFarm(state, {name, current_farm}) {
      let old_farm = await fetch(`${URL}farms/${current_farm.farm}`, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())

      old_farm.name = name

      let response = await fetch(`${URL}farms/${current_farm.farm}/`, {
        method: "PUT",
        body: JSON.stringify(old_farm),
        headers: this.getters.getHeader
      })
      .then(response => response.ok)
      .catch(() => false)

      if (response) {
        state.commit("updateEditFarm", {current_farm, name})
      }

      return response
    },
    async endBatch(state, {batch, date_departure, final}) {
      const id = await batch.id == null ? batch.pk : batch.id
      let old_batch = await fetch(`${URL}batches/${id}`, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())

      const updatedBatch = state.getters.getBatchTarget
      updatedBatch.date_departure = date_departure

      old_batch.date_departure = date_departure
      old_batch.average_final_weight = final
      const losses = old_batch.batch_losses.length == null ? old_batch.batch_losses : old_batch.batch_losses.length
      old_batch.final_count = old_batch.initial_count - losses

      let put_response = await fetch(`${URL}batches/${id}/`, {
        method: "PUT",
        body: JSON.stringify(old_batch),
        headers: this.getters.getHeader
      })
      .then(response => response.ok)

      if (put_response) {
        state.commit('updateBatchTarget', updatedBatch)
      }

      return {'ok': put_response, 'batch': batch}
    },
    async editBatch(state, {batch, accommodation, initial, desired, count, date_birth, days_life, animal_type}) {
      const batch_pk = batch.id == null ? batch.pk : batch.id

      let old_batch = await fetch(`${URL}batches/${batch_pk}`, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())

      old_batch.date_accomodation = accommodation
      old_batch.average_initial_weight = initial
      old_batch.desired_weight = desired
      old_batch.initial_count = count
      if (date_birth) old_batch.date_birth = date_birth
      if (days_life) old_batch.days_life = days_life
      if (animal_type != null && animal_type !== '') {
        old_batch.animal_type = Number(animal_type)
      }

      let put_response = await fetch(`${URL}batches/${batch_pk}/`, {
        method: "PUT",
        body: JSON.stringify(old_batch),
        headers: this.getters.getHeader
      })
      .then(response => response.ok)

      const barn_pk = state.getters.getBarnTarget.pk
      const accommodation_pk = old_batch.batch_accommodation[0]
      const date = accommodation
     
      if (put_response) {
        const batchTarget = this.getters.getBatchTarget
        if (batchTarget && (batchTarget.pk === batch_pk || batchTarget.id === batch_pk)) {
          if (animal_type != null && animal_type !== '') {
            batchTarget.animal_type = Number(animal_type)
          }
          batchTarget.date_accomodation = accommodation
          batchTarget.average_initial_weight = initial
          batchTarget.desired_weight = desired
          batchTarget.initial_count = count
          if (date_birth) batchTarget.date_birth = date_birth
          if (days_life) batchTarget.days_life = days_life
        }
        state.dispatch('fetchBatchDetail', batch_pk)
        await state.dispatch("editAccommodation", {accommodation_pk, barn_pk, batch_pk, date})
      }

      return {'ok': put_response, 'batch': batch}
    },
    async registerWeight(state, {batch, weight, datetime_registered}) {
      const data = {
        batch: batch.id,
        weight: weight,
        datetime_fetched: datetime_registered
      }
      await fetch(`${URL}batch_manual_weights/`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => {
        if (response.ok) {
          state.commit("setError", false)
        }
        else {
          state.commit("setError", true)
        }
      })
      .catch(() => state.commit("setError", true))
    },
    async setAllBatches(state) {
      const level = state.getters.getUserLevel
      let reportURL = null
      if (level === "pecsmart") {
        reportURL = `${URL}farms/report/history/pecsmart`
      }
      if (level === "company") {
        reportURL = `${URL}farms/report/history/company/${this.getters.getCurrentUser.company}`
      }
      if (level === "employee") {
        const associated_farm = this.getters.getCurrentUser.associated_farm
        reportURL = `${URL}farms/report/history/employee/${this.getters.getCurrentUser.company}/${associated_farm}`
      }
      await fetch(reportURL, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        state.commit("setAllBatches", json)
      })
    },
    async setCurrentUser(state, username) {
      await fetch(`${URL}users/report/${username}`, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        window.localStorage.setItem("role", json[0].role)
        state.commit("setCurrentUser", json[0]);
      })
      .catch(() => state.commit("setError", true))
    },
    async setFarm(state) {
      const level = state.getters.getUserLevel
      let reportURL = null
      if (level === "pecsmart") {
        reportURL = `${URL}farms/report/home/pecsmart`
      }
      if (level === "company") {
        reportURL = `${URL}farms/report/home/company/${this.getters.getCurrentUser.company}`
      }
      if (level === "employee") {
        const associated_farm = this.getters.getCurrentUser.associated_farm
        reportURL = `${URL}farms/report/home/employee/${this.getters.getCurrentUser.company}/${associated_farm}`
      }
      await fetch(reportURL, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        console.log('json', json)
        state.commit("setFarm", json)
      })
    },
    async setSelectedFarms(state, value) {
      state.commit("setSelectedFarms", value);
    },
    async login(state, {user, isLoginPage}) {
      try {
        if (isLoginPage) {
          await this.dispatch("setToken", user)
        }
        await this.dispatch("setCurrentUser", user.username)
        const role = state.getters.getCurrentUser.role
        this.commit("setIsAdmin", role)
        this.commit("setUserLevel", role)
        state.commit("setNavbar", true)
        if (router.history.current.name !== "HomePage") {
          router.push('home-page')
        }
      }
      catch(err) {
        this.commit("setError", true)
      }
    },
    async startHome(state) {
      try {
        state.commit("setLogStatus", true)
        // Cargar estado persistente de selects
        state.dispatch("loadPersistentState")
        
        // Establecer estado inicial por defecto para usuarios pecsmart si no hay estado persistente
        if (this.getters.getUserLevel === 'pecsmart') {
          if (!state.selectedCompany && !state.selectedFarm && !state.selectedFarmPecsmart) {
            // Estado inicial: mostrar por granjas (Pecsmart Support) por defecto
            state.commit('setShowByFarm', true)
            state.commit('setShowByCompanies', false)
          }
        }
        
        await this.dispatch("fetchFarms", this.getters.getCurrentUser.company)
      }
      catch(err) {
        this.commit("setError", true)
      }
    },
    async cleanStore(state) {
      state.commit("setDefaultValues")
    },
    async chartDisplay (state, chart) {
      state.commit("chartDisplay", chart)
    },
    async logout () {
      await fetch(`${URL}auth/logout/`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8"
        }
      })
    },
    async fetchSmartMicData(state, batch_pk) {
      const cached = state.getters.getSmartMicData.find(item => String(item?.batch) === String(batch_pk))
      if (cached) return cached
      await fetch(`${URL}coughs/batch_report/${batch_pk}/`, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        json.batch = batch_pk
        state.commit("setSmartMicData", json)
      })
      .catch(err => console.log(err))
    },
    async fetchFeedData(state, {barn, accommodation, departure, silo}) {
      const cacheKey = `${barn}|${silo}|${accommodation}|${departure}`
      const cachedFeedData = this.getters.getCachedSmartFeedDataByKey(cacheKey)
      if (cachedFeedData) {
        state.commit("setSmartFeedData", cachedFeedData)
        return cachedFeedData
      }

      try {
        const response = await fetch(`${URL}silo_feed_weights/report/${barn}/${silo}/${accommodation}/${departure}`, {
          headers: this.getters.getHeader
        })
        const json = await response.json()
        state.commit("setSmartFeedData", json)
        state.commit("setSmartFeedDataByKey", { key: cacheKey, data: json })
        return json
      } catch (error) {
        throw error || "error"
      }
    },
    async fetchFeedConsolidatedData(state, {barn, accommodation, departure}) {
      const cacheKey = `${barn}|${accommodation}|${departure}`
      const cachedConsolidatedData = this.getters.getCachedSmartFeedConsolidatedDataByKey(cacheKey)
      if (cachedConsolidatedData) {
        state.commit("setSmartFeedConsolidatedData", cachedConsolidatedData)
        return cachedConsolidatedData
      }

      try {
        const response = await fetch(`${URL}silo_feed_weights/report/consume/${barn}/${accommodation}/${departure}/`, {
          headers: this.getters.getHeader,
        });
    
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.details || "An error occurred");
        }
    
        // Obtener el texto de la respuesta para limpiar valores problemáticos
        const responseText = await response.text();
        
        // Limpiar valores Infinity y NaN por 0 para evitar errores de JSON
        let cleanedResponseText = responseText;
        if (responseText.includes('Infinity') || responseText.includes('NaN')) {
          cleanedResponseText = responseText
            .replace(/:\s*Infinity/g, ': 0')
            .replace(/:\s*NaN/g, ': 0');
        }
        
        // Parsear el JSON limpio
        const json = JSON.parse(cleanedResponseText);
        const data = json.data.sort((a, b) => new Date(a.date) - new Date(b.date));
        json.data = data;
        
        state.commit("setSmartFeedConsolidatedData", json);
        state.commit("setSmartFeedConsolidatedDataByKey", { key: cacheKey, data: json });
        return json;
        
      } catch (error) {
        if (error.message.includes('Unexpected token')) {
          state.commit('setErrorMessage', "Error no servidor");
        } else {
          state.commit('setErrorMessage', error.message);
        }
        throw error;
      }
    },
    async fetchFeedChartData(state, { barn, silo, startDate, endDate }) {
      const cacheKey = `${barn}|${silo}|${startDate}|${endDate}`
      const cachedChartData = this.getters.getCachedSmartFeedChartDataByKey(cacheKey)
      if (cachedChartData) return cachedChartData

      try {
        const response = await fetch(`${URL}silo_feed_weights/report/${barn}/${silo}/${startDate}/${endDate}`, {
          headers: this.getters.getHeader
        })
        const json = await response.json()
        const payload = json?.detail
          ? {
              silo: silo,
              barn: barn,
              data: [],
              level_history: [],
              message: json
            }
          : json

        state.commit("setSmartFeedChartDataByKey", { key: cacheKey, data: payload })
        return payload
      } catch (error) {
        console.log(error)
        throw error
      }
    },
    async fetchAnimalTypes(state, company) {
      if (this.getters.getAnimalTypes != null) return;
      let url_to_fetch = this.getters.getUserLevel === 'pecsmart' ? `${URL}animal_type/` : `${URL}animal_type/?sex=&company=${company}`
      await fetch(url_to_fetch, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        state.commit("setAnimalTypes", json)
      })
      .catch(err => console.log(err))
    },
    async fecthLossesByBatch(state, {batch, url}) {
      const batchPk = batch == null ? null : String(batch)
      if (batchPk == null) return []

      if (url == null) {
        const cachedLosses = this.getters.getCachedLossesByBatch(batchPk)
        if (cachedLosses?.length) {
          state.commit("setLossesBatchData", cachedLosses)
          return cachedLosses
        }
      }

      let url_to_fetch = alignUrlWithApiBase(
        url == null ? apiUrl(`losses/?batch=${batchPk}`) : url
      )
      const allLosses = []

      try {
        while (url_to_fetch) {
          const response = await fetch(url_to_fetch, {
            headers: mergeApiHeaders(this.getters.getHeader),
          })
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }
          const json = await response.json()
          if (Array.isArray(json)) {
            allLosses.push(...json)
            break
          }
          allLosses.push(...extractApiList(json))
          url_to_fetch = json.next ? alignUrlWithApiBase(json.next) : null
        }

        state.commit("setLossesBatchData", allLosses)
        state.commit("setLossesBatchByBatch", { batchPk, losses: allLosses })
        return allLosses
      } catch (err) {
        console.error('fecthLossesByBatch:', err)
        return allLosses
      }
     },
     async fetchFarms(state, company) {
      let url_to_fetch = apiRoutes.farmHomeByCompany(company)
      await fetch(url_to_fetch, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        json.sort((a, b) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        });

        state.commit("setFarm", json)

      })
      .catch(err => console.log(err))
    },
    async fetchCamData(state, batch) {
      const batchPk = batch == null ? null : String(batch)
      if (batchPk == null) return []

      const cachedCamData = this.getters.getCachedSmartCamData(batchPk)
      if (cachedCamData?.length) {
        this.commit('setCamData', cachedCamData)
        return cachedCamData
      }

      try {
        const response = await fetch(
          apiUrl(`batch_weights/batch_weight_report/${batchPk}/?format=json`),
          { headers: mergeApiHeaders(this.getters.getHeader) }
        )

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const json = await response.json()
        const rows = extractApiList(json)
        if (!rows.length) return []

        const camData = [...rows].reverse()
        this.commit('setCamData', camData)
        this.commit('setCamDataByBatch', { batchPk, data: camData })
        return camData
      } catch (err) {
        console.error('fetchCamData:', err)
        return []
      }
    },
    async fetchIndividualWeighingData(state, { barnPk, batchPk, labels, force = false } = {}) {
      const pk = barnPk == null ? null : String(barnPk)
      const publicBatchPk = batchPk == null ? null : String(batchPk)
      if (pk == null && publicBatchPk == null) return null

      const cacheKey = pk || `batch-${publicBatchPk}`
      if (!force) {
        const cached = this.getters.getCachedIndividualWeighingData(cacheKey)
        if (cached && (!publicBatchPk || cached.penWeights)) {
          const data = cached.penWeights && labels
            ? {
              ...cached,
              granja: labels.granja || cached.granja,
              galpon: labels.galpon || cached.galpon,
              empresa: labels.empresa || cached.empresa,
            }
            : cached
          this.commit('setIndividualWeighingData', data)
          return data
        }
      }

      if (publicBatchPk) {
        const response = await fetch(
          `${URL_CAMV3}api/batches/${publicBatchPk}/pen-weights`,
          {
            headers: {
              Accept: 'application/json',
              Authorization: this.getters.getHeader?.Authorization,
            },
          }
        )

        if (response.ok) {
          const json = await response.json()
          const data = mapPenWeightsResponse(json, labels)
          this.commit('setIndividualWeighingData', data)
          this.commit('setIndividualWeighingDataByBarn', { barnPk: cacheKey, data })
          return data
        }

        if (response.status !== 404) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
      }

      if (pk == null) return null

      // Endpoint antigo, por baia. Sem Authorization: o preflight CORS desse
      // serviço não libera o header e o navegador bloqueia a requisição.
      const response = await fetch(
        apiRoutes.individualWeighingByBarn(pk),
        { headers: { Accept: 'application/json' } }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const json = await response.json()
      const data = normalizeIndividualWeighingPayload(json)
      this.commit('setIndividualWeighingData', data)
      this.commit('setIndividualWeighingDataByBarn', { barnPk: pk, data })
      return data
    },
    async fetchIndividualWeighingPen(state, { batchPk, pen, barnPk } = {}) {
      if (batchPk == null || pen == null) return null

      const headers = {
        Accept: 'application/json',
        Authorization: this.getters.getHeader?.Authorization,
      }
      const urls = [
        `${URL_CAMV3}api/batches/${batchPk}/pens/${pen}/weights`,
        `${URL_CAMV3}api/batches/${batchPk}/pen-weights?pen=${pen}`,
      ]

      let json = null
      for (const url of urls) {
        const response = await fetch(url, { headers })
        if (response.ok) {
          json = await response.json()
          break
        }
      }
      if (!json) return null

      this.commit('mergeIndividualWeighingPenSeries', { barnPk, pen, payload: json })
      return this.getters.getIndividualWeighingData
    },
    async fetchBatchDetail(state, batch) {
      let url_to_fetch = apiRoutes.batchHomeByBatch(batch)
      await fetch(url_to_fetch, {
        headers: this.getters.getHeader,
      })
      .then(response => response.json())
      .then(json => {
        this.commit('setBatchDetail', json)
      })
      .catch(err => console.log(err))
    },
    async fetchReferenceWeightCurve(state, animalType) {
      if (animalType == null) {
        this.commit('setCurveWeightReference', [])
        return []
      }

      const cachedCurve = this.getters.getCachedCurveWeightReference(animalType)
      if (cachedCurve !== null && cachedCurve !== undefined) {
        this.commit('setCurveWeightReference', cachedCurve)
        return cachedCurve
      }

      let url_to_fetch = apiUrl(`animal_type/${animalType}/`)
      try {
        const response = await fetch(url_to_fetch, {
          headers: this.getters.getHeader
        })
        const json = await response.json()
        const curve = Array.isArray(json.weight_gain_reference_curve)
          ? json.weight_gain_reference_curve
          : []
        this.commit('setCurveWeightReference', curve)
        this.commit('setCurveWeightReferenceByAnimalType', { animalType, curve })
        return curve
      } catch (err) {
        console.log(err)
        this.commit('setCurveWeightReference', [])
        return []
      }
    },
    async fetchCompanySettings(state, company) {
      let url_to_fetch = `${URL}company_settings/?company=${company}`
      await fetch(url_to_fetch, {
        headers: this.getters.getHeader
      })
      .then(response => response.json())
      .then(json => {
        const settings = json?.results?.[0]
        if (settings) {
          this.commit('setCompanySettings', settings)
        }
      })
      .catch(err => console.log(err))
    },
    async fetchAllPaginatedData(state, { url, params = {} }) {
      try {
        const allResults = [];
        let currentUrl = url;
        
        while (currentUrl) {
          const requestUrl = alignUrlWithApiBase(currentUrl);
          const response = await fetch(requestUrl, {
            headers: this.getters.getHeader,
            ...params
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          allResults.push(...data.results);
          currentUrl = data.next;
        }
        
        return allResults;
      } catch (error) {
        console.error('Error fetching paginated data:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async fetchCompanies() {
      let url_to_fetch = `${URL}companies/?active=true&ordering=name`;
      const companies = await this.dispatch('fetchAllPaginatedData', {
        url: url_to_fetch
      });
      this.commit("setCompanies", companies);
    },
    async fetchCompanyById(state, company_pk) {
      try {
        const response = await fetch(`${URL}companies/${company_pk}/`, {
          headers: this.getters.getHeader
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const company = await response.json();
        return company;
      } catch (error) {
        console.error('Error fetching company by id:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async fetchFoodByCompany(state, company) {
      try {
        if (state.state.foodsByCompany[company]) {
          state.commit("setFoodByCompany", state.state.foodsByCompany[company]);
          return state.state.foodsByCompany[company];
        }
        
        let url_to_fetch = `${URL}foods/?name=&company=${company}`;
        const foods = await this.dispatch('fetchAllPaginatedData', {
          url: url_to_fetch
        });
        state.commit("setFoodsByCompany", { companyPk: company, foods });
        state.commit("setFoodByCompany", foods);
        return foods;
      } catch (error) {
        console.error('Error fetching foods by company:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async fetchCamV3(state, batch) {
      try {
        const data = await retrySeveralTimes({
          url: `${URL_CAMV3}get_predictions_for_batch/${batch}`,
          retryAttempts: 2,
          delay: 2000
        });
        
        if (data.status) {
          this.commit("setCamV3Data", data.status);
        } else {
          this.commit("setCamV3Data", data);
        }
      } catch (error) {
        console.error('Error after all retries:', error);
      }
    },
    async registerFood(state, {name, density, selectedSilos, company}) {
      const data = {
        name: name,
        density: density,
        silos: selectedSilos,
        company: company,
      }

      await fetch(`${URL}foods/`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => {
        if (response.ok) {
          state.commit("setError", false)
        }
        else {
          state.commit("setError", true)
        }
      })
      .catch(() => state.commit("setError", true))
    },
    async changeFood(state, {food, company, newDate, silo}) {
      const data = {
        food: food,
        company: company,
        silo: silo,
        datetime: newDate,
      }

      await fetch(`${URL}sensors/report/data/home/switch_food/`, {
        method: "POST",
        body: JSON.stringify(data),
        headers: this.getters.getHeader
      })
      .then(response => {
        if (response.ok) {
          state.commit("setError", false)
        }
        else {
          state.commit("setError", true)
        }
      })
      .catch(() => state.commit("setError", true))
    },
    async updateLoss(state, editedLoss) {
      try {
        const requestUrl = alignUrlWithApiBase(editedLoss.url);
        const response = await fetch(requestUrl, {
          method: 'PATCH',
          headers: this.getters.getHeader,
          body: JSON.stringify({
            pk: editedLoss.pk,
            batch: editedLoss.batch,
            weight: editedLoss.weight,
            quantity: editedLoss.quantity,
            reason: editedLoss.reason,
            death_details: editedLoss.death_details,
            death_datetime: editedLoss.death_datetime,
            external_id: editedLoss.external_id,
            company_pk: editedLoss.company_pk
          })
        });

        state.commit('updateLossesEdit', editedLoss)

        if (!response.ok) {
          throw new Error('Error al actualizar la baixa');
        }
        state.commit('setError', false);
      } catch (error) {
        console.error('Error updating loss:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async deleteLoss(state, loss) {
      try {
        const requestUrl = alignUrlWithApiBase(loss.url);
        
        const response = await fetch(requestUrl, {
          method: 'DELETE',
          headers: this.getters.getHeader
        });

        state.commit('updateLossesDelete', loss)

        if (!response.ok) {
          throw new Error('Error al eliminar a baixa');
        }

        state.commit('setError', false);
      } catch (error) {
        console.error('Error deleting loss:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    loadPersistentState(state) {
      try {
        const savedState = localStorage.getItem('pecsmart-selects-state')
        if (savedState) {
          const parsedState = JSON.parse(savedState)
          state.commit('setSelectedCompany', parsedState.selectedCompany)
          state.commit('setSelectedFarm', parsedState.selectedFarm)
          state.commit('setSelectedFarmPecsmart', parsedState.selectedFarmPecsmart)
          state.commit('setShowByFarm', parsedState.showByFarm)
          state.commit('setShowByCompanies', parsedState.showByCompanies)
        }
      } catch (error) {
        console.error('Error loading persistent state:', error)
      }
    },
    async setTargetCompanyAction(state, company) {
      state.commit('setTargetCompany', company)
    },
    async barnHome(state, barn_pk) {
      try {
        const response = await fetch(apiRoutes.barnSensorsHome(barn_pk), {
          headers: this.getters.getHeader
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error fetching barn home:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    filterVirtualSensorsBySilo(state, sensors) {
      const sensorsBySilo = {};
      
      sensors.forEach(sensor => {
        if (sensor.silo) {
          const siloId = typeof sensor.silo === 'number' ? sensor.silo : sensor.silo.pk;
          
          if (!sensorsBySilo[siloId]) {
            sensorsBySilo[siloId] = [];
          }
          sensorsBySilo[siloId].push(sensor);
        }
      });
      
      const filteredSensors = [];
      
      Object.keys(sensorsBySilo).forEach(siloKey => {
        const siloSensors = sensorsBySilo[Number(siloKey)];
        
        if (siloSensors.length > 1) {
          const virtualSensors = siloSensors.filter(sensor => sensor.virtual === true);
          filteredSensors.push(...virtualSensors);
        } else {
          filteredSensors.push(...siloSensors);
        }
      });
      
      return filteredSensors;
    },
    async fetchSiloSettings(state, { silo_pk }) {
      try {
        if (silo_pk == null) return []
        const cachedSettings = this.getters.getCachedSiloSettings(silo_pk)
        if (cachedSettings) return cachedSettings

        const response = await fetch(apiRoutes.siloSettings(silo_pk), {
          method: "GET",
          headers: this.getters.getHeader
        })
        if (!response.ok) {
          throw new Error(`Error loading silo settings: ${response.status}`)
        }
        const data = await response.json()
        const settings = data?.results || []
        state.commit('setSiloSettingsBySilo', { siloPk: silo_pk, settings })
        return settings
      } catch (error) {
        console.error('Error fetching silo settings:', error)
        throw error
      }
    },
    async fetchSiloProfileHistory(state, { sensor_pk, start_date, end_date }) {
      try {
        let normalizedStartDate = normalizeDateTimeForApi(start_date);
        let normalizedEndDate = normalizeDateTimeForApi(inclusiveHistoryEnd(end_date));
        if (normalizedStartDate && normalizedEndDate && new Date(normalizedStartDate) > new Date(normalizedEndDate)) {
          [normalizedStartDate, normalizedEndDate] = [normalizedEndDate, normalizedStartDate];
        }
        const cacheKey = `${sensor_pk}|${normalizedStartDate}|${normalizedEndDate}`
        const cachedProfiles = this.getters.getCachedSiloProfileHistory(cacheKey)
        if (cachedProfiles && cachedProfiles.length) return cachedProfiles

        const headers = this.getters.getHeader
        const profiles = await this.dispatch('fetchSiloProfileHistoryWindows', {
          sensor_pk,
          start_date: normalizedStartDate,
          end_date: normalizedEndDate,
          headers,
        })
        state.commit('setSiloProfileHistoryByKey', { key: cacheKey, profiles })
        return profiles;
      } catch (error) {
        console.error('Error fetching silo profile history:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async fetchSiloProfileHistoryWindows(state, { sensor_pk, start_date, end_date, headers, depth = 0 }) {
      const startMs = new Date(start_date).getTime();
      const endMs = new Date(end_date).getTime();
      const windows = historyRangeChunks(startMs, endMs);
      if (!windows.length) {
        return fetchSiloProfileHistoryWindow(headers, sensor_pk, start_date, end_date);
      }

      const groups = [];
      for (let index = 0; index < windows.length; index += 1) {
        const [windowStartMs, windowEndMs] = windows[index];
        const windowStart = format(new Date(windowStartMs), "yyyy-MM-dd'T'HH:mm:ss");
        const windowEnd = format(new Date(windowEndMs), "yyyy-MM-dd'T'HH:mm:ss");
        let batch = await fetchSiloProfileHistoryWindow(headers, sensor_pk, windowStart, windowEnd);
        if (depth < 6 && historyResponseLooksTruncated(batch, windowStartMs, windowEndMs)) {
          const midMs = windowStartMs + Math.floor((windowEndMs - windowStartMs) / 2);
          const mid = format(new Date(midMs), "yyyy-MM-dd'T'HH:mm:ss");
          const [left, right] = await Promise.all([
            this.dispatch('fetchSiloProfileHistoryWindows', {
              sensor_pk,
              start_date: windowStart,
              end_date: mid,
              headers,
              depth: depth + 1,
            }),
            this.dispatch('fetchSiloProfileHistoryWindows', {
              sensor_pk,
              start_date: mid,
              end_date: windowEnd,
              headers,
              depth: depth + 1,
            }),
          ]);
          batch = mergeProfileHistory([batch, left, right]);
        }
        const uncoveredEdges = depth < 6
          ? historyUncoveredEdges(batch, windowStartMs, windowEndMs)
          : [];
        if (uncoveredEdges.length) {
          const fills = [];
          for (let edgeIndex = 0; edgeIndex < uncoveredEdges.length; edgeIndex += 1) {
            const [edgeStartMs, edgeEndMs] = uncoveredEdges[edgeIndex];
            fills.push(await this.dispatch('fetchSiloProfileHistoryWindows', {
              sensor_pk,
              start_date: format(new Date(edgeStartMs), "yyyy-MM-dd'T'HH:mm:ss"),
              end_date: format(new Date(edgeEndMs), "yyyy-MM-dd'T'HH:mm:ss"),
              headers,
              depth: depth + 1,
            }));
          }
          const merged = mergeProfileHistory([batch, ...fills]);
          if (merged.length > batch.length) batch = merged;
        }
        groups.push(batch);
      }
      return mergeProfileHistory(groups);
    },
    async fetchSiloProfileCurvesMap(state, { sensor_pk, start_date, end_date }) {
      try {
        let formattedStart = normalizeDateTimeForApi(start_date);
        let formattedEnd = normalizeDateTimeForApi(end_date);
        if (formattedStart && formattedEnd && new Date(formattedStart) > new Date(formattedEnd)) {
          [formattedStart, formattedEnd] = [formattedEnd, formattedStart];
        }
        const cacheKey = `${sensor_pk}|${formattedStart}|${formattedEnd}`
        const cachedCurvesMap = this.getters.getCachedSiloProfileCurvesMap(cacheKey)
        if (cachedCurvesMap) return cachedCurvesMap
        const curvesApiUrl = (process.env.VUE_APP_TEST_URL ? process.env.VUE_APP_TEST_URL : URL).replace(/\/?$/, '/');
        const response = await fetch(`${curvesApiUrl}silo_profiles/silo_profile_history/`, {
          method: 'POST',
          headers: {
            ...this.getters.getHeader,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            start_date: formattedStart,
            end_date: formattedEnd,
            sensor_pk,
            curves_v2: true,
            bypass_validation: true,
          }),
        });

        if (!response.ok) {
          throw new Error(`Error al cargar curvas: ${response.status}`);
        }

        const data = await response.json();
        const list = Array.isArray(data) ? data : (data?.results ?? []);
        const curvesMap = list.reduce((acc, item) => {
          if (item.id != null && item.silo_profile_curves_v2__curves) {
            acc[item.id] = item.silo_profile_curves_v2__curves;
          }
          return acc;
        }, {});
        state.commit('setSiloProfileCurvesMapByKey', { key: cacheKey, curvesMap })
        return curvesMap
      } catch (error) {
        console.error('Error fetching curves map:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async fetchSiloLastProfileBySilo(state, { silo_pk, start_date, end_date }) {
      try {
        let formattedStartDate = normalizeDateTimeForApi(start_date);
        let formattedEndDate = normalizeDateTimeForApi(end_date);
        if (formattedStartDate && formattedEndDate && new Date(formattedStartDate) > new Date(formattedEndDate)) {
          [formattedStartDate, formattedEndDate] = [formattedEndDate, formattedStartDate];
        }
        const cacheKey = `${silo_pk}|${formattedStartDate}|${formattedEndDate}`
        const cachedLastProfile = this.getters.getCachedSiloLastProfileBySilo(cacheKey)
        if (cachedLastProfile) return cachedLastProfile
        const response = await fetch(apiRoutes.siloLastProfileBySilo(silo_pk, formattedStartDate, formattedEndDate), {
          headers: this.getters.getHeader,
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(`Error al cargar último perfil: ${response.status}`);
        }

        const profile = await response.json();
        state.commit('setSiloLastProfileBySiloByKey', { key: cacheKey, profile })
        return profile;
      } catch (error) {
        console.error('Error fetching last profile by silo:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async fetchBarnProfilesHistory(state, { barn_pk, start_date, end_date, company_pk }) {
      try {
        const barnData = await this.dispatch('barnHome', barn_pk);
        if (!barnData) {
          console.warn('No se encontraron sensores para el barn:', barn_pk);
          return { profilesData: [], company: null, foods: [] };
        }

        const filteredSensors = await this.dispatch('filterVirtualSensorsBySilo', barnData);
        if (filteredSensors.length === 0) {
          console.warn('No hay sensores después del filtrado');
          return { profilesData: [], company: null, foods: [] };
        }

        const profilePromises = filteredSensors.map(sensor => {
          return this.dispatch('fetchSiloProfileHistory', {
            sensor_pk: sensor.pk,
            start_date,
            end_date
          }).catch(error => {
            console.error(`Error fetching profiles for sensor ${sensor.pk}:`, error);
            return [];
          });
        });

        const profilesData = await Promise.all(profilePromises);

        const user = this.getters.getCurrentUser;

        let foods = [];
        try {
          const cachedFoods = this.getters.getFoodsByCompany(company_pk);
          
          if (cachedFoods && cachedFoods.length > 0) {
            foods = cachedFoods;
          } else {
            foods = await this.dispatch('fetchFoodByCompany', company_pk);
          }
        } catch (error) {
          console.error('Error fetching foods, using empty array:', error);
          foods = [];
        }

        return {
          profilesData,
          user,
          foods
        };
      } catch (error) {
        console.error('Error fetching barn profiles history:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async generateExcelReport(state, { profilesData, foods, companyName, farmName, barnName, startDate, endDate }) {
      try {
        await generateProfileReport(
          profilesData,
          foods,
          companyName,
          farmName,
          barnName,
          startDate,
          endDate,
          getMinSiloLevelVariationPercent(this.getters.getCompanySettings)
        );
      } catch (error) {
        console.error('Error generating Excel report:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async excelBarnProfileHistory(state, { barn_pk, start_date, end_date, company_pk, farm_name, barn_name }) {
      try {
        const result = await this.dispatch('fetchBarnProfilesHistory', {
          barn_pk,
          start_date,
          end_date,
          company_pk
        });

        const { profilesData, user, foods } = result;

        if (!profilesData || profilesData.length === 0 || profilesData.every(arr => arr.length === 0)) {
          throw new Error('Não há dados de perfis disponíveis para o período selecionado');
        }

        const companyName = user?.company_name || 'Empresa';

        await this.dispatch('generateExcelReport', {
          profilesData,
          foods,
          companyName,
          farmName: farm_name,
          barnName: barn_name,
          startDate: start_date,
          endDate: end_date
        });

        return { success: true };
      } catch (error) {
        console.error('Error in excelBarnProfileHistory:', error);
        state.commit('setError', true);
        throw error;
      }
    },
    async fetchFeedInventoryBatches({ commit, dispatch, getters }, company) {
      if (getters.getFeedInventoryFetched(company)) {
        return getters.getFeedInventoryOpenBatches(company)
      }

      commit('clearFeedInventoryBatches', company)
      commit('setFeedInventoryBatchesLoading', { company, loading: true })

      try {
        const allBatches = await dispatch('fetchAllPaginatedData', {
          url: `${URL}batches/?is_closed=false&animal_type__company=${company}`
        })

        const entries = dedupeOpenBatchesByFarm(allBatches, getters.getAllBarns || [])

        for (const entry of entries) {
          commit('addFeedInventoryBatch', { company, batch: entry })
        }

        return getters.getFeedInventoryOpenBatches(company)
      } catch (error) {
        console.error('Feed inventory: error fetching open batches', error)
        return []
      } finally {
        commit('setFeedInventoryBatchesLoading', { company, loading: false })
        commit('setFeedInventoryFetched', { company, fetched: true })
      }
    },
    async fetchFeedInventoryConsumeByBatch({ commit, getters }, batchPk) {
      try {
        const data = await fetchConsumeByBatchReport(getters.getHeader, batchPk)
        commit('setFeedInventoryConsume', { batchPk, data })
        return data
      } catch (error) {
        console.error('Feed inventory: error fetching consume by batch', batchPk, error)
        return []
      }
    },
  },
  modules: {},
  getters: {
    getCurrentUser: state => state.currentUser,
    getAllFarms: state => state.Farms,
    getAllBatches: state => state.allBatches,
    getSelectedFarms: state => state.selectedFarms,
    getLogedIn: state => state.logedIn,
    getError: state => state.error,
    getKillWeight: state => state.killWeight,
    getManualWeights: state => state.manualWeights,
    getWeightChartStatus: state => state.weightChartStatus,
    getStandardDeviationChartStatus: state => state.standardDeviationChartStatus,
    getUniformityChartStatus: state => state.uniformityChartStatus,
    getGpdWeekChartStatus: state => state.gpdWeekChartStatus,
    getGpdAccChartStatus: state => state.gpdAccChartStatus,
    // Falls back to localStorage so a request fired before the header is
    // explicitly rehydrated (e.g. a component mounted directly on a
    // deep-link navigation) still carries the Authorization header instead
    // of going out as null.
    getHeader: state => state.header || buildAuthHeader(window.localStorage.getItem('Token')),
    getLoadingStateSmartFeed: state => state.loadingStateSmartFeed,
    getSmartMicData: state => state.smartMicData,
    getSmartFeedData: state => state.smartFeedData,
    getSmartFeedDataByKey: state => state.smartFeedDataByKey,
    getSmartCamData : state => state.smartCamData,
    getSmartCamDataByBatch: state => state.smartCamDataByBatch,
    getSensorsListFeed : state => state.sensorsListFeed,
    getSensorsListMic : state => state.sensorsListMic,
    getSensorsListCam : state => state.sensorsListCam,
    getBatchDetail : state => state.batchDetail,
    getCurveWeightReference: state=> state.curveWeightReference,
    getCurveWeightReferenceByAnimalType: state => state.curveWeightReferenceByAnimalType,
    getActiveRequest: state => state.activeRequests,
    getAllBarns: state => state.allBarns,
    getStateBarns: state => state.stateBarns,
    getHistoryBatch: state => state.historyBatch,
    getCompanySettings: state => state.companySettings,
    getIndividualWeighingData: state => state.individualWeighingData,
    getCachedIndividualWeighingData: (state) => (barnPk) => state.individualWeighingDataByBarn[barnPk],
    getLossesBatch: state => state.lossesBatch,
    getLossesBatchByBatch: state => state.lossesBatchByBatch,
    getFoodByCompany: state => state.foodByCompany,
    getLossesChartStatus: state => state.lossesChartStatus,
    getCamv3Data: state => state.dataCamv3,
    getCompanies: state => state.companies,
    getNavbar: state => state.navbar,
    getBarnTarget: state => state.barn_target,
    getFarmTarget: state => state.farm_target,
    getBatchTarget: state => state.batch_target,
    getTargetCompany: state => state.targetCompany,
    getIsAdmin: state => state.isAdmin,
    getUserLevel: state => state.userLevel,
    getSmartFeedConsolidatedData: state => state.smartFeedConsolidatedData,
    getSmartFeedConsolidatedDataByKey: state => state.smartFeedConsolidatedDataByKey,
    getSmartFeedChartDataByKey: state => state.smartFeedChartDataByKey,
    getAnimalTypes: state => state.animalTypes,
    getErrorMessage: state => state.errorMessage,
    // Nuevos getters para persistencia de selects
    getSelectedCompany: state => state.selectedCompany,
    getSelectedFarm: state => state.selectedFarm,
    getSelectedFarmPecsmart: state => state.selectedFarmPecsmart,
    getShowByFarm: state => state.showByFarm,
    getShowByCompanies: state => state.showByCompanies,
    getSiloDataByBarn: state => state.siloDataByBarn,
    getSiloDataHomeByBarn: state => state.siloDataHomeByBarn,
    getSiloSettingsBySilo: state => state.siloSettingsBySilo,
    getSiloProfileHistoryByKey: state => state.siloProfileHistoryByKey,
    getSiloProfileCurvesMapByKey: state => state.siloProfileCurvesMapByKey,
    getSiloLastProfileBySiloByKey: state => state.siloLastProfileBySiloByKey,
    getSensorLoadingStates: state => state.sensorLoadingStates,
    getHomeFarmBarnsCache: state => state.homeFarmBarnsCache,
    getHomeBarnDataCache: state => state.homeBarnDataCache,
    getAccFoodDataByBarn: state => state.accFoodDataByBarn,
    getFoodsByCompany: state => companyPk => {
      if (companyPk) {
        return state.foodsByCompany[companyPk] || [];
      }
      return state.foodsByCompany;
    },
    getCachedSiloData: (state) => (barnPk, siloPk, maxAgeMinutes = 5) => {
      
      if (!state.siloDataByBarn[barnPk]) {
        return null;
      }
      
      if (!state.siloDataByBarn[barnPk][siloPk]) {
        return null;
      }
      
      const cachedData = state.siloDataByBarn[barnPk][siloPk];
      
      if (cachedData.lastUpdated) {
        const lastUpdate = new Date(cachedData.lastUpdated);
        const now = new Date();
        const diffMinutes = (now - lastUpdate) / 1000 / 60;
        
        
        if (diffMinutes < maxAgeMinutes) {
          return cachedData.originalData;
        }
      }
      return null;
    },
    getCachedSiloDataHome: (state) => (barnPk, siloPk, maxAgeMinutes = 5) => {
      
      if (!state.siloDataHomeByBarn[barnPk]) {
        return null;
      }
      
      if (!state.siloDataHomeByBarn[barnPk][siloPk]) {
        return null;
      }
      
      const cachedData = state.siloDataHomeByBarn[barnPk][siloPk];
      
      if (cachedData.lastUpdated) {
        const lastUpdate = new Date(cachedData.lastUpdated);
        const now = new Date();
        const diffMinutes = (now - lastUpdate) / 1000 / 60;
        
        
        if (diffMinutes < maxAgeMinutes) {
          return cachedData.originalData;
        }
        return null;
      }
      return null;
    },
    getCachedHomeFarmBarns: (state) => (farmPk, maxAgeMinutes = 15) => {
      if (farmPk == null) return null
      const cached = state.homeFarmBarnsCache[farmPk]
      if (!cached) return null
      if (!cached.lastUpdated) return cached.barns || []
      const diffMinutes = (new Date() - new Date(cached.lastUpdated)) / 1000 / 60
      if (diffMinutes < maxAgeMinutes) return cached.barns || []
      return null
    },
    getCachedHomeBarnData: (state) => (barnPk, maxAgeMinutes = 10) => {
      if (barnPk == null) return null
      const cached = state.homeBarnDataCache[barnPk]
      if (!cached) return null
      if (!cached.lastUpdated) return cached
      const diffMinutes = (new Date() - new Date(cached.lastUpdated)) / 1000 / 60
      if (diffMinutes < maxAgeMinutes) return cached
      return null
    },
    getCachedAccFoodDataByBarn: (state) => (barnPk, maxAgeMinutes = 30) => {
      if (barnPk == null) return null
      const cached = state.accFoodDataByBarn[barnPk]
      if (!cached) return null
      if (!cached.lastUpdated) return cached
      const diffMinutes = (new Date() - new Date(cached.lastUpdated)) / 1000 / 60
      if (diffMinutes < maxAgeMinutes) return cached
      return null
    },
    getCachedSmartCamData: (state) => (batchPk) => {
      if (batchPk == null) return null
      const cached = state.smartCamDataByBatch[String(batchPk)]
      if (!cached?.data?.length) return null
      return cached.data
    },
    getCachedCurveWeightReference: (state) => (animalType) => {
      if (animalType == null) return null
      const cached = state.curveWeightReferenceByAnimalType[animalType]
      if (!cached) return null
      return cached.curve || []
    },
    getCachedLossesByBatch: (state) => (batchPk) => {
      if (batchPk == null) return null
      const cached = state.lossesBatchByBatch[String(batchPk)]
      if (!cached?.losses?.length) return null
      return cached.losses
    },
    getCachedSmartFeedDataByKey: (state) => (key) => {
      if (!key) return null
      const cached = state.smartFeedDataByKey[key]
      if (!cached) return null
      return cached.data || null
    },
    getCachedSmartFeedConsolidatedDataByKey: (state) => (key) => {
      if (!key) return null
      const cached = state.smartFeedConsolidatedDataByKey[key]
      if (!cached) return null
      return cached.data || null
    },
    getCachedSmartFeedChartDataByKey: (state) => (key) => {
      if (!key) return null
      const cached = state.smartFeedChartDataByKey[key]
      if (!cached) return null
      return cached.data || null
    },
    getCachedSiloSettings: (state) => (siloPk) => {
      if (siloPk == null) return null
      const cached = state.siloSettingsBySilo[siloPk]
      if (!cached) return null
      return cached.settings || []
    },
    getCachedSiloProfileHistory: (state) => (key) => {
      if (!key) return null
      const cached = state.siloProfileHistoryByKey[key]
      if (!cached) return null
      return cached.profiles || []
    },
    getCachedSiloProfileCurvesMap: (state) => (key) => {
      if (!key) return null
      const cached = state.siloProfileCurvesMapByKey[key]
      if (!cached) return null
      return cached.curvesMap || {}
    },
    getCachedSiloLastProfileBySilo: (state) => (key) => {
      if (!key) return null
      const cached = state.siloLastProfileBySiloByKey[key]
      if (!cached) return null
      return cached.profile || null
    },
    getFeedInventoryOpenBatches: (state) => (company) => {
      return state.feedInventoryBatchesByCompany[company] || []
    },
    getFeedInventoryBatchesLoading: (state) => (company) => {
      return state.feedInventoryBatchesLoadingByCompany[company] || false
    },
    getFeedInventoryFetched: (state) => (company) => {
      return state.feedInventoryFetchedByCompany[company] || false
    },
    getFeedInventoryBarnSensors: (state) => (barnPk) => {
      return state.feedInventoryBarnSensorsByBarn[barnPk] || []
    },
    getFeedInventoryConsume: (state) => (batchPk) => {
      return state.feedInventoryConsumeByBatch[batchPk] || []
    },
    getFeedInventoryDiasPrioridade: (state) => (batchPk) => {
      return state.feedInventoryDiasPrioridadeByBatch[batchPk] || 0
    },
    getFeedInventoryBatchLoading: (state) => (batchPk) => {
      return state.feedInventoryLoadingByBatch[batchPk] || false
    },
    getFeedInventoryAllSensors: (state) => {
      return state.feedInventoryBarnSensorsByBarn
    }
  }
})